import {NgComponentOutlet, NgTemplateOutlet} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, effect, inject, viewChild} from '@angular/core';
import {Drawer, DrawerModule} from 'primeng/drawer';

import {TranslatePipe} from '@mydeals/i18n';

import {DrawerSize} from './drawer-config.model';
import {DrawerService} from './drawer-service';

const SIZE_TO_WIDTH: Record<DrawerSize, string> = {
  sm: '320px',
  md: '480px',
  lg: '640px',
};

@Component({
  selector: 'crm-drawer-host',
  imports: [DrawerModule, NgComponentOutlet, NgTemplateOutlet, TranslatePipe],
  template: `
    @if (state(); as s) {
      <p-drawer
        [visible]="s.ref.visible()"
        [position]="s.config.position ?? 'right'"
        [modal]="s.config.modal ?? true"
        [dismissible]="s.config.dismissible ?? true"
        [closeOnEscape]="s.config.closeOnEscape ?? true"
        [closable]="false"
        [style]="widthStyle()"
        (visibleChange)="onVisibleChange($event)"
        (onHide)="onHide()"
      >
        <ng-template pTemplate="headless">
          <div class="drawer">
            <header class="drawer__header">
              <h2 class="drawer__title">
                @if (s.ref.title(); as title) {
                  {{ title | translate }}
                }
              </h2>
              <button
                type="button"
                class="drawer__close"
                [attr.aria-label]="'common.close' | translate"
                (click)="s.ref.requestClose()"
              >
                <i class="pi pi-times"></i>
              </button>
            </header>

            <div class="drawer__body">
              <ng-container *ngComponentOutlet="s.componentType; injector: s.injector" />
            </div>

            @if (s.ref.footer(); as footerTpl) {
              <footer class="drawer__footer">
                <ng-container *ngTemplateOutlet="footerTpl" />
              </footer>
            }
          </div>
        </ng-template>
      </p-drawer>
    }
  `,
  styleUrl: './drawer-host.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawerHost {
  private readonly drawerService = inject(DrawerService);

  readonly state = this.drawerService.state;

  private readonly drawer = viewChild(Drawer);

  readonly widthStyle = computed(() => {
    const size = this.state()?.config.size ?? 'md';
    return {width: SIZE_TO_WIDTH[size]};
  });

  constructor() {
    // A new drawer mounts hidden; flip it visible next microtask to play the enter animation.
    effect(() => {
      const s = this.state();
      if (s) {
        queueMicrotask(() => s.ref.show());
      }
    });
  }

  /**
   * Backdrop click / Escape. PrimeNG has already started tearing the backdrop down (leave
   * animation, removed on `animationend`) by the time it emits, so a close guard that says
   * "stay" has to put it back — dropped outright first, since `enableModality()` is a no-op
   * while the old, still-animating mask element is referenced.
   */
  onVisibleChange(visible: boolean): void {
    const ref = this.state()?.ref;
    if (visible || !ref) return;
    void ref.requestClose().then((closed) => {
      const drawer = this.drawer();
      if (closed || ref.closed || !drawer) return;
      drawer.destroyModal();
      drawer.enableModality();
    });
  }

  onHide(): void {
    // Leave animation finished — now remove the drawer from the DOM.
    if (this.state()?.ref.closed) {
      this.drawerService.destroyActive();
    }
  }
}
